import { useState, useEffect, useCallback } from "react";
import api from "../../services/api";

function fmtDate(d) { if (!d) return "—"; return new Date(d).toLocaleDateString("fr-FR"); }
function pct(n) { return n ? `${Math.round(n * 10) / 10}%` : "0%"; }

// ── Config Panel ──────────────────────────────────────────────────────────
function BrevoConfigPanel({ currentCompanyId, cfg, onSaved }) {
  const [form, setForm] = useState({
    api_key: cfg?.api_key || "", sender_name: cfg?.sender_name || "FinanceApp",
    sender_email: cfg?.sender_email || "", is_active: cfg?.is_active || false,
    template_invoice: cfg?.template_invoice || "", template_relance: cfg?.template_relance || "",
    template_payment: cfg?.template_payment || "", template_portal: cfg?.template_portal || "",
    template_trial_end: cfg?.template_trial_end || "", template_renewal: cfg?.template_renewal || "",
  });
  const [testing, setTesting]   = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [saving, setSaving]     = useState(false);
  const [templates, setTemplates] = useState([]);
  const [syncing, setSyncing]   = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const testConnection = async () => {
    if (!form.api_key) return;
    setTesting(true); setTestResult(null);
    try {
      const r = await api.abonnements.testBrevo(form.api_key);
      setTestResult({ ok: true, msg: `✅ Connexion réussie — ${r.account?.email || ""}` });
      // Load templates
      if (r.ok && currentCompanyId) {
        api.abonnements.brevoTemplates(currentCompanyId).then(setTemplates).catch(() => {});
      }
    } catch (err) { setTestResult({ ok: false, msg: `❌ ${err.message}` }); }
    finally { setTesting(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.abonnements.saveBrevoConfig({ ...form, company_id: currentCompanyId });
      onSaved();
    } finally { setSaving(false); }
  };

  const handleSync = async () => {
    setSyncing(true); setSyncResult(null);
    try {
      const r = await api.abonnements.brevoSync({ company_id: currentCompanyId });
      setSyncResult(`✅ ${r.synced} contact(s) synchronisé(s)${r.errors?.length ? ` · ${r.errors.length} erreur(s)` : ""}`);
    } catch (err) { setSyncResult(`❌ ${err.message}`); }
    finally { setSyncing(false); }
  };

  const TEMPLATE_FIELDS = [
    ["template_invoice", "📄 Nouvelle facture"],
    ["template_relance", "📨 Relance impayé"],
    ["template_payment", "💳 Confirmation paiement"],
    ["template_portal", "🔑 Invitation portail client"],
    ["template_trial_end", "⏰ Fin d'essai"],
    ["template_renewal", "🔄 Renouvellement fournisseur"],
  ];

  return (
    <>
      {/* Rappels non envoyés faute de configuration — point 3 du diagnostic
          Abonnements récurrents : rendre visible côté admin ce qui, avant,
          échouait silencieusement (confirmations de paiement, alertes fin
          d'essai/renouvellement fournisseur). Disparaît dès qu'une clé API
          est sauvegardée (skipped_count remis à 0 par POST /brevo-config). */}
      {!cfg?.api_key && cfg?.skipped_count > 0 && (
        <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, padding: "12px 16px", marginBottom: 16, display: "flex", gap: 10, alignItems: "flex-start" }}>
          <span style={{ fontSize: 18 }}>⚠️</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#92400e" }}>
              {cfg.skipped_count} email{cfg.skipped_count > 1 ? "s" : ""} non envoyé{cfg.skipped_count > 1 ? "s" : ""} faute de configuration Brevo
            </div>
            <div style={{ fontSize: 12, color: "#92400e", marginTop: 2 }}>
              Confirmations de paiement, alertes de fin d'essai et de renouvellement fournisseur n'ont pas pu être envoyées à vos clients
              {cfg.last_skipped_at ? ` (dernier cas le ${new Date(cfg.last_skipped_at).toLocaleString("fr-FR")})` : ""}.
              Configurez votre clé API ci-dessous pour les réactiver.
            </div>
          </div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
      {/* API Config */}
      <div className="card">
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16 }}>🔧 Configuration Brevo</div>
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Clé API Brevo</label>
          <input className="input" type="password" value={form.api_key} onChange={e => set("api_key", e.target.value)} placeholder="xkeysib-..." />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Nom de l'expéditeur</label>
          <input className="input" value={form.sender_name} onChange={e => set("sender_name", e.target.value)} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Email expéditeur</label>
          <input className="input" type="email" value={form.sender_email} onChange={e => set("sender_email", e.target.value)} />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <input type="checkbox" checked={form.is_active} onChange={e => set("is_active", e.target.checked)} />
            <span style={{ fontSize: 13, fontWeight: 600 }}>Activer l'intégration Brevo</span>
          </label>
        </div>
        {testResult && (
          <div style={{ padding: "8px 12px", background: testResult.ok ? "#1a9e7215" : "#e24b4a15", borderRadius: 8, fontSize: 13, marginBottom: 12, color: testResult.ok ? "#1a9e72" : "#e24b4a" }}>{testResult.msg}</div>
        )}
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn" disabled={testing || !form.api_key} onClick={testConnection}>{testing ? "Test…" : "🧪 Tester la connexion"}</button>
          <button className="btn primary" disabled={saving} onClick={handleSave}>{saving ? "Sauvegarde…" : "💾 Sauvegarder"}</button>
        </div>
      </div>

      {/* Sync */}
      <div className="card">
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16 }}>🔄 Synchronisation contacts</div>
        <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 12 }}>
          Synchronise automatiquement vos contacts vers Brevo :
          <ul style={{ marginTop: 8, paddingLeft: 18, lineHeight: 2 }}>
            <li>Clients FinanceApp → liste "Clients FinanceApp"</li>
            <li>Abonnés actifs → liste "Abonnés Dropshipping"</li>
            <li>Abonnés expirés → liste "Abonnés à relancer"</li>
            <li>Prospects CRM → liste "Prospects"</li>
          </ul>
        </div>
        {cfg?.last_sync_at && <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 12 }}>Dernière sync : {new Date(cfg.last_sync_at).toLocaleString("fr-FR")}</div>}
        {syncResult && <div style={{ padding: "8px 12px", background: syncResult.includes("✅") ? "#1a9e7215" : "#e24b4a15", borderRadius: 8, fontSize: 13, marginBottom: 12, color: syncResult.includes("✅") ? "#1a9e72" : "#e24b4a" }}>{syncResult}</div>}
        <button className="btn primary" disabled={syncing || !cfg?.api_key} onClick={handleSync}>{syncing ? "Synchronisation…" : "⚡ Forcer la synchronisation"}</button>

        {/* Templates */}
        <div style={{ marginTop: 20, borderTop: "1px solid #f3f4f6", paddingTop: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12 }}>Templates emails transactionnels</div>
          {TEMPLATE_FIELDS.map(([k, l]) => (
            <div key={k} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <label style={{ fontSize: 12, flex: 1, fontWeight: 500 }}>{l}</label>
              <select className="input" style={{ width: 160 }} value={form[k]||""} onChange={e => set(k, e.target.value)}>
                <option value="">— Sans template —</option>
                {templates.map(t => <option key={t.id} value={t.id}>{t.name} (#{t.id})</option>)}
                {form[k] && !templates.find(t => String(t.id) === String(form[k])) && <option value={form[k]}>#{form[k]}</option>}
              </select>
            </div>
          ))}
        </div>
      </div>
      </div>
    </>
  );
}

// ── Campaign Creator ──────────────────────────────────────────────────────
function NewCampaignModal({ currentCompanyId, cfg, onClose, onCreated }) {
  const [lists, setLists]   = useState([]);
  const [templates, setTemplates] = useState([]);
  const [form, setForm]     = useState({
    name: "", subject: "", list_ids: [], html_content: "", template_id: "", scheduled_at: "", use_template: false,
  });
  const [preview, setPreview] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState("");

  useEffect(() => {
    if (!cfg?.api_key) return;
    api.abonnements.brevoLists(currentCompanyId).then(setLists).catch(console.error);
    api.abonnements.brevoTemplates(currentCompanyId).then(setTemplates).catch(console.error);
  }, [cfg, currentCompanyId]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleList = (id) => {
    const ids = form.list_ids.includes(id) ? form.list_ids.filter(x => x !== id) : [...form.list_ids, id];
    set("list_ids", ids);
  };

  const handleCreate = async (sendNow = false) => {
    if (!form.name || !form.subject || form.list_ids.length === 0) { setError("Nom, objet et au moins une liste requis."); return; }
    setSaving(true); setError("");
    try {
      const r = await api.abonnements.createCampaign({ ...form, company_id: currentCompanyId });
      if (sendNow && r.id) await api.abonnements.sendCampaign(r.id, currentCompanyId);
      onCreated(); onClose();
    } catch (err) { setError(err.response?.data?.error || err.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "#0009", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", overflowY: "auto", padding: "40px 16px" }}>
      <div className="card" style={{ width: "100%", maxWidth: 680 }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 20 }}>
          <span style={{ fontWeight: 700, fontSize: 16 }}>📧 Nouvelle campagne Brevo</span>
          <button className="btn" style={{ marginLeft: "auto" }} onClick={onClose}>✕</button>
        </div>
        {error && <div style={{ background: "#e24b4a22", color: "#e24b4a", padding: "8px 12px", borderRadius: 8, marginBottom: 12, fontSize: 13 }}>{error}</div>}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
          <div style={{ gridColumn: "1/-1" }}>
            <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Nom de la campagne *</label>
            <input className="input" value={form.name} onChange={e => set("name", e.target.value)} placeholder="Newsletter Juin 2026" />
          </div>
          <div style={{ gridColumn: "1/-1" }}>
            <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Objet email *</label>
            <input className="input" value={form.subject} onChange={e => set("subject", e.target.value)} placeholder="Votre offre exclusive ce mois" />
          </div>
          <div style={{ gridColumn: "1/-1" }}>
            <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Listes destinataires *</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {lists.map(l => (
                <button key={l.id} type="button" onClick={() => toggleList(l.id)} style={{ padding: "4px 10px", borderRadius: 16, fontSize: 12, fontWeight: 600, cursor: "pointer", border: `1px solid ${form.list_ids.includes(l.id) ? "#1a9e72" : "#e5e7eb"}`, background: form.list_ids.includes(l.id) ? "#1a9e7215" : "#fff", color: form.list_ids.includes(l.id) ? "#1a9e72" : "var(--text-muted)" }}>
                  {form.list_ids.includes(l.id) ? "✓ " : ""}{l.name} ({l.totalSubscribers || 0})
                </button>
              ))}
              {lists.length === 0 && <span style={{ fontSize: 12, color: "var(--text-muted)" }}>Aucune liste Brevo — synchronisez d'abord vos contacts.</span>}
            </div>
          </div>

          {/* Template or HTML */}
          <div style={{ gridColumn: "1/-1" }}>
            <div style={{ display: "flex", gap: 12, marginBottom: 10 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                <input type="radio" checked={!form.use_template} onChange={() => set("use_template", false)} /> Éditeur HTML
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                <input type="radio" checked={form.use_template} onChange={() => set("use_template", true)} /> Template Brevo
              </label>
            </div>
            {form.use_template ? (
              <select className="input" value={form.template_id} onChange={e => set("template_id", e.target.value)}>
                <option value="">— Sélectionner un template —</option>
                {templates.map(t => <option key={t.id} value={t.id}>{t.name} (#{t.id})</option>)}
              </select>
            ) : (
              <>
                <textarea className="input" rows={6} value={form.html_content} onChange={e => set("html_content", e.target.value)} placeholder="<h1>Bonjour {{contact.FIRSTNAME}} !</h1><p>Votre message ici...</p>" style={{ fontFamily: "monospace", fontSize: 12, resize: "vertical" }} />
                {form.html_content && (
                  <button type="button" className="btn" style={{ fontSize: 11, marginTop: 4 }} onClick={() => setPreview(!preview)}>
                    {preview ? "Masquer" : "👁️ Aperçu"}
                  </button>
                )}
                {preview && form.html_content && (
                  <div style={{ marginTop: 8, padding: 16, border: "1px solid #e5e7eb", borderRadius: 8, background: "#fff" }} dangerouslySetInnerHTML={{ __html: form.html_content }} />
                )}
              </>
            )}
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Planifier l'envoi (optionnel)</label>
            <input className="input" type="datetime-local" value={form.scheduled_at} onChange={e => set("scheduled_at", e.target.value)} />
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="btn" onClick={onClose}>Annuler</button>
          <button className="btn" disabled={saving} onClick={() => handleCreate(false)}>{saving ? "…" : "💾 Enregistrer brouillon"}</button>
          <button className="btn primary" disabled={saving} onClick={() => handleCreate(true)}>{saving ? "Envoi…" : "🚀 Envoyer maintenant"}</button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Main MailingTab
// ════════════════════════════════════════════════════════════════════════
export default function MailingTab({ currentCompanyId }) {
  const [innerTab, setInnerTab]   = useState("config");
  const [cfg, setCfg]             = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showNew, setShowNew]     = useState(false);

  const load = useCallback(() => {
    if (!currentCompanyId) return;
    setLoading(true);
    Promise.all([
      api.abonnements.brevoConfig(currentCompanyId),
      cfg?.api_key ? api.abonnements.brevoCampaigns(currentCompanyId) : Promise.resolve({ campaigns: [] }),
    ]).then(([c, camp]) => {
      setCfg(c);
      setCampaigns(camp?.campaigns || []);
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, [currentCompanyId]);

  useEffect(() => { load(); }, [load]);

  const loadCampaigns = () => {
    if (!cfg?.api_key) return;
    api.abonnements.brevoCampaigns(currentCompanyId).then(r => setCampaigns(r?.campaigns || [])).catch(console.error);
  };

  const INNER_TABS = [
    { id: "config",    label: "⚙️ Configuration" },
    { id: "campaigns", label: "📧 Campagnes" },
  ];

  if (loading) return <div style={{ textAlign: "center", padding: 48, color: "var(--text-muted)" }}>Chargement…</div>;

  return (
    <div>
      <div style={{ display: "flex", gap: 2, borderBottom: "2px solid #e5e7eb", marginBottom: 20 }}>
        {INNER_TABS.map(t => (
          <button key={t.id} onClick={() => setInnerTab(t.id)} style={{ padding: "7px 16px", fontSize: 13, fontWeight: innerTab === t.id ? 700 : 500, cursor: "pointer", border: "none", background: "transparent", color: innerTab === t.id ? "var(--g)" : "var(--text-muted)", borderBottom: `2px solid ${innerTab === t.id ? "var(--g)" : "transparent"}`, marginBottom: -2 }}>
            {t.label}
          </button>
        ))}
      </div>

      {innerTab === "config" && <BrevoConfigPanel currentCompanyId={currentCompanyId} cfg={cfg} onSaved={load} />}

      {innerTab === "campaigns" && (
        <div>
          {!cfg?.api_key ? (
            <div style={{ padding: "32px 24px", textAlign: "center", background: "#f9fafb", borderRadius: 12, fontSize: 13, color: "var(--text-muted)" }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>🔑</div>
              Configurez votre clé API Brevo dans l'onglet Configuration pour gérer les campagnes.
            </div>
          ) : (
            <>
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
                <button className="btn primary" onClick={() => setShowNew(true)}>📧 Nouvelle campagne</button>
              </div>
              {campaigns.length === 0 ? (
                <div className="card" style={{ textAlign: "center", padding: 56 }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>📧</div>
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>Aucune campagne envoyée</div>
                  <button className="btn primary" onClick={() => setShowNew(true)}>Créer la première campagne</button>
                </div>
              ) : (
                <div className="card" style={{ padding: 0, overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 740 }}>
                    <thead>
                      <tr style={{ background: "#f8fafc", fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase" }}>
                        {["Campagne", "Envoyée le", "Destinataires", "Ouvertures", "Clics", "Statut"].map(h => (
                          <th key={h} style={{ padding: "9px 14px", textAlign: "left", fontWeight: 600 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {campaigns.map(c => {
                        const stats = c.statistics?.campaignStats?.[0] || {};
                        return (
                          <tr key={c.id} style={{ borderTop: "1px solid #f3f4f6", fontSize: 13 }}>
                            <td style={{ padding: "10px 14px" }}>
                              <div style={{ fontWeight: 700 }}>{c.name}</div>
                              <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{c.subject}</div>
                            </td>
                            <td style={{ padding: "10px 14px", fontSize: 12, color: "var(--text-muted)" }}>{fmtDate(c.sentDate || c.createdAt)}</td>
                            <td style={{ padding: "10px 14px", fontWeight: 700 }}>{stats.delivered || stats.sent || "—"}</td>
                            <td style={{ padding: "10px 14px" }}>
                              {stats.uniqueOpens !== undefined ? (
                                <><div style={{ fontWeight: 700 }}>{stats.uniqueOpens}</div><div style={{ fontSize: 10, color: "var(--text-muted)" }}>{pct(stats.openRate)}</div></>
                              ) : "—"}
                            </td>
                            <td style={{ padding: "10px 14px" }}>
                              {stats.clicks !== undefined ? (
                                <><div style={{ fontWeight: 700 }}>{stats.clicks}</div><div style={{ fontSize: 10, color: "var(--text-muted)" }}>{pct(stats.clickRate)}</div></>
                              ) : "—"}
                            </td>
                            <td style={{ padding: "10px 14px" }}>
                              <span style={{ fontSize: 11, padding: "3px 8px", borderRadius: 8, fontWeight: 700, background: c.status === "sent" ? "#1a9e7215" : "#d9770615", color: c.status === "sent" ? "#1a9e72" : "#d97706" }}>
                                {c.status === "sent" ? "Envoyée" : c.status || "Brouillon"}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {showNew && <NewCampaignModal currentCompanyId={currentCompanyId} cfg={cfg} onClose={() => setShowNew(false)} onCreated={() => { loadCampaigns(); setShowNew(false); }} />}
    </div>
  );
}
